import { Alert, Box, CircularProgress, DialogContentText, Grid } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { useAppDispatch, useAppSelector } from "hooks"
import { DropzoneArea } from "mui-file-dropzone"
import Papa from "papaparse"
import { useState } from "react"
import userService from "services/userService"
import { addUser } from "slices/usersSlice"
import type { IOrganization } from "types/organizationTypes"
import { UserRoles } from "types/userRoles"
import type { ICreateUser } from "types/userTypes"

type Props = {
  organization: IOrganization
}

type IExampleUserCsv = {
  user_group_name: string
  user_role: string
  pin: string
  firstname: string
  lastname: string
  email: string
  phonenumber: string
  locale: string
}

export function CreateUsersBatchDialog({ organization }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)
  const [csvFile, setCsvFile] = useState<File>()
  const [error, setError] = useState<string>()

  const userGroups = useAppSelector((state) => state.usergroup.userGroups)
  const dispatch = useAppDispatch()

  const handleSubmit = () => {
    setIsLoading(true)

    if (csvFile === undefined) {
      setIsLoading(false)
      return
    }

    setError(undefined)
    const createUsersBatch: ICreateUser[] = []

    Papa.parse<IExampleUserCsv>(csvFile, {
      header: true,
      complete: (results) => {
        results.data.forEach((data, _rowIndex) => {
          const userRow = data as IExampleUserCsv

          const userGroupId = userGroups.find((x) => x.name === userRow.user_group_name)?.id

          if (userGroupId === undefined) {
            setError(`User group ${userRow.user_group_name} not found.`)
            setIsLoading(false)
          } else {
            const userRole =
              userRow.user_role === UserRoles.ORGANIZATION_ADMIN || userRow.user_role === "Admin"
                ? UserRoles.ORGANIZATION_ADMIN
                : UserRoles.USER

            const createUser: ICreateUser = {
              organizationId: organization.id,
              userGroupId: userGroupId,
              role: userRole,
              pin: userRow.pin,
              firstname: userRow.firstname,
              lastname: userRow.lastname,
              email: userRow.email,
              phonenumber: userRow.phonenumber,
              locale: userRow.locale,
            }

            createUsersBatch.push(createUser)
          }
        })

        userService
          .createUsersBatch(createUsersBatch)
          .then((users) => {
            users.forEach((user) => {
              dispatch(addUser(user))
            })

            setIsLoading(false)
            handleClose()
          })
          .catch((err) => {
            setError(
              "Error creating new users. Make sure users PIN or Email does not already exist.",
            )

            console.log(err)
            setIsLoading(false)
          })
      },
    })
  }

  const handleClickOpen = () => {
    setError(undefined)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const downloadExcampleCsv = () => {
    const exampleCsv: IExampleUserCsv = {
      user_group_name: "Default User Group",
      user_role: "User",
      pin: "123456",
      firstname: "Test",
      lastname: "User",
      email: "test.user@example.com",
      phonenumber: "+123 123 1234567",
      locale: "Perrysburg",
    }

    var csv = Papa.unparse([exampleCsv])
    var csvData = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    })
    var csvURL = null

    csvURL = window.URL.createObjectURL(csvData)

    var tempLink = document.createElement("a")
    tempLink.href = csvURL
    tempLink.setAttribute("download", "user_import_template.csv")
    tempLink.click()
  }

  return (
    <div>
      <Button data-testid="importUsersButton" variant="outlined" onClick={handleClickOpen}>
        import users
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <DialogTitle>Import users</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DialogContentText>
                  For adding multiple users, follow these steps:
                </DialogContentText>
                <DialogContentText>1. Download the CSV template.</DialogContentText>
                <DialogContentText>2. Populate the template with user details.</DialogContentText>
                <DialogContentText>3. Upload the filled CSV file here.</DialogContentText>
              </Grid>
              <Grid item xs={12}>
                <DialogContentText>
                  <Button variant="outlined" onClick={downloadExcampleCsv}>
                    Download CSV TEMPLATE
                  </Button>
                </DialogContentText>
              </Grid>
              <Grid item xs={12}>
                <DropzoneArea
                  onChange={(files) => {
                    setCsvFile(files[0])
                  }}
                  filesLimit={1}
                  fileObjects={csvFile}
                />
              </Grid>
            </Grid>
            {error !== undefined && <Alert severity="error">{error}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button data-testid="cancelButton" onClick={handleClose}>
              Cancel
            </Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button onClick={handleSubmit} disabled={isLoading}>
                Create
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
